import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Spinner } from "@phosphor-icons/react";
import PublisherRow from "./PublisherRow";
import { PublisherStats, PublisherToolbar, PublisherActions } from "./PublisherToolbar";
import { PUB_FILTERS, norm, shortMissing } from "./publisherShared";

const EMPTY_STATS = { total: 0, published: 0, ready_unpublished: 0, no_photo: 0 };

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  return d.message || fallback;
};

const aiSummary = (applied) => {
  const parts = [];
  if (applied.specs?.length) parts.push(`ficha +${applied.specs.length}`);
  if (applied.storefront?.length) parts.push(applied.storefront.join(", "));
  return parts.length ? `Alendra completó: ${parts.join(" · ")}` : "Nada que completar con IA (foto/precio requieren acción manual)";
};

// Pestaña "Publicador masivo": productos con stock, % de listo, completar con
// Alendra y publicar varios a la vez.
export default function StorefrontPublisher({ categories, canUseAlendra, onEdit, refreshKey }) {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("unpublished");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [selected, setSelected] = useState(() => new Set());
  const [ai, setAi] = useState({});
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [publishing, setPublishing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/products/publisher");
      setRows(data.items || []);
      setStats(data.stats || EMPTY_STATS);
    } catch (err) {
      toast.error(errText(err, "No se pudo cargar el publicador"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const recomputeStats = (list) => setStats({
    total: list.length,
    published: list.filter((r) => r.show_in_storefront).length,
    ready_unpublished: list.filter((r) => r.readiness.ready && !r.show_in_storefront).length,
    no_photo: list.filter((r) => !r.photo).length,
  });
  const replaceRow = (row) => setRows((rs) => { const next = rs.map((r) => (r.id === row.id ? row : r)); recomputeStats(next); return next; });

  const base = useMemo(() => {
    const nq = norm(q);
    return rows.filter((r) => (cat === "all" || r.category === cat) && (!nq || norm(`${r.name} ${r.brand} ${r.sku} ${r.category}`).includes(nq)));
  }, [rows, q, cat]);
  const counts = useMemo(() => Object.fromEntries(PUB_FILTERS.map((f) => [f.key, base.filter(f.test).length])), [base]);
  const visible = useMemo(() => base.filter(PUB_FILTERS.find((f) => f.key === filter)?.test || (() => true)), [base, filter]);
  const visibleIds = visible.map((r) => r.id);
  const allChecked = visible.length > 0 && visibleIds.every((id) => selected.has(id));
  const readyVisible = visible.filter((r) => r.readiness.ready && !r.show_in_storefront);

  const setChecked = (id, v) => setSelected((s) => { const n = new Set(s); v ? n.add(id) : n.delete(id); return n; });
  const toggleAll = (v) => setSelected((s) => { const n = new Set(s); visibleIds.forEach((id) => (v ? n.add(id) : n.delete(id))); return n; });
  const selectReady = () => setSelected(new Set(readyVisible.map((r) => r.id)));
  const selectedRows = rows.filter((r) => selected.has(r.id));

  const completeWithAI = async () => {
    const targets = selectedRows.filter((r) => !r.readiness.ready);
    if (targets.length === 0) return toast.info("Los seleccionados ya están al 100%.");
    setRunning(true);
    setProgress({ done: 0, total: targets.length });
    let nowReady = 0;
    for (let i = 0; i < targets.length; i++) {
      const r = targets[i];
      setAi((a) => ({ ...a, [r.id]: { status: "loading" } }));
      try {
        const { data } = await api.post(`/alendra/products/${r.id}/complete-storefront`);
        replaceRow(data.row);
        if (data.row.readiness.ready) nowReady += 1;
        setAi((a) => ({ ...a, [r.id]: { status: "done", summary: aiSummary(data.applied || {}) } }));
      } catch (err) {
        setAi((a) => ({ ...a, [r.id]: { status: "error", summary: errText(err, "Alendra no pudo completar este producto") } }));
      }
      setProgress({ done: i + 1, total: targets.length });
    }
    setRunning(false);
    toast.success(`Alendra revisó ${targets.length} producto(s) · ${nowReady} quedaron listos para publicar`, {
      duration: 8000,
      action: nowReady > 0 ? { label: "Publicar listos", onClick: () => publish(true, true) } : undefined,
    });
  };

  const publish = async (publishFlag, onlyReady = false, ids = null) => {
    let targetIds = ids || [...selected];
    if (onlyReady) targetIds = rows.filter((r) => targetIds.includes(r.id) && r.readiness.ready && !r.show_in_storefront).map((r) => r.id);
    if (targetIds.length === 0) return toast.info("No hay productos para esta acción.");
    setPublishing(true);
    try {
      const { data } = await api.post("/products/publish-storefront/bulk", { ids: targetIds, publish: publishFlag });
      const doneSet = new Set(data.done || []);
      setRows((rs) => {
        const next = rs.map((r) => (doneSet.has(r.id) ? { ...r, show_in_storefront: publishFlag, storefront_published_at: publishFlag ? new Date().toISOString() : null } : r));
        recomputeStats(next);
        return next;
      });
      const skipped = data.skipped || [];
      setAi((a) => {
        const n = { ...a };
        doneSet.forEach((id) => { delete n[id]; });
        skipped.forEach((s) => { n[s.id] = { status: "skipped", summary: `No publicado: falta ${s.missing.map(shortMissing).slice(0, 3).join(", ")}${s.missing.length > 3 ? "…" : ""}` }; });
        return n;
      });
      if (!ids) setSelected((s) => { const n = new Set(s); doneSet.forEach((id) => n.delete(id)); return n; });
      const verb = publishFlag ? "publicado" : "despublicado";
      if (doneSet.size) toast.success(`${doneSet.size} producto(s) ${verb}(s) en la tienda`);
      if (skipped.length) toast.warning(`${skipped.length} no se publicaron: aún les falta información`);
    } catch (err) {
      toast.error(errText(err, "No se pudo completar la acción"));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="storefront-publisher">
      <PublisherStats stats={stats} filter={filter} setFilter={setFilter} />
      <PublisherToolbar q={q} setQ={setQ} cat={cat} setCat={setCat} categories={categories} filter={filter} setFilter={setFilter} counts={counts} />
      <PublisherActions visible={visible} selected={selected} allChecked={allChecked} toggleAll={toggleAll} selectReady={selectReady} readyCount={readyVisible.length}
        canUseAlendra={canUseAlendra} running={running} progress={progress} publishing={publishing}
        onComplete={completeWithAI} onPublish={() => publish(true)} onUnpublish={() => publish(false)} />
      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <div className="hidden md:grid grid-cols-[auto_auto_minmax(0,2fr)_72px_110px_minmax(0,1.6fr)_120px_auto] gap-x-3 px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-50 border-b border-zinc-200">
          <span className="w-4" /><span className="w-12" /><span>Producto</span><span className="text-center">Stock</span><span>Tienda</span><span>Listo para publicar</span><span>Estado</span><span />
        </div>
        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500 inline-flex items-center gap-2 w-full justify-center" data-testid="pub-loading"><Spinner size={16} className="animate-spin" /> Cargando…</div>
        ) : visible.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-500 italic" data-testid="pub-empty">
            {rows.length === 0 ? "No hay productos con stock." : "Nada que mostrar con este filtro."}
          </div>
        ) : (
          <ul data-testid="pub-list">
            {visible.map((r) => (
              <PublisherRow key={r.id} row={r} ai={ai[r.id]} checked={selected.has(r.id)} onCheck={(v) => setChecked(r.id, v)}
                onEdit={() => onEdit(r.id)} onToggle={() => publish(!r.show_in_storefront, false, [r.id])} busy={publishing || running} canUseAlendra={canUseAlendra} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
